import { Injectable } from '@angular/core';
import { ExportContext, ExportFileFormat, ExportRange, ExportService } from 'app/layout/export-overlay';
import { buildQuery, page, seed } from '@albatrosdeveloper/ave-utils-npm/lib/utils/query.util';
import { takeUntil } from 'rxjs';
import get from 'lodash-es/get';
import ceil from 'lodash-es/ceil';
import { LOCALIZED_PROPERTIES_ADDRESS, BUSINESS_PARTNER_ADDRESS_EXPORT_ID, BUSINESS_PARTNER_ADDRESS_TITLE, LOCALIZED_PROPERTIES_ALL_ADDRESS } from './business-partner.constants';
import { BusinessPartnerService } from '../business-partner.service';
import BusinessPartnerAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity';

@Injectable()
export class BusinessPartnerAddressExport {
  constructor(
    private exportService: ExportService,
    private businessPartnerService: BusinessPartnerService
  ) {}

  //cambiar atributo
  private transformItem(item: BusinessPartnerAttributes) {
    const externalKeys = get(item, "externalKeys.code", "");
    const name = get(item, "name", "");
    const addresses = get(item, "addresses", []);

    if (!Array.isArray(addresses) || addresses.length === 0) {
      return [
        {
          externalKeys,
          name,
          address: "",
          longitude: "",
          latitude: "",
          addressType: "",
          country: "",
          department: "",
          city: "",
          district: "",
          isPrincipal: "",
        },
      ];
    }

    return addresses.map((address) => ({
      externalKeys,
      name,
      address: get(address, "address", ""),
      latitude: get(address, "geolocationPoint[0]", ""),
      longitude: get(address, "geolocationPoint[1]", ""),
      addressType: get(address, "type.name", ""),
      country: get(address, "locationOne.name", ""),
      department: get(address, "locationTwo.name", ""),
      city: get(address, "locationThree.name", ""),
      district: get(address, "locationFour.name", ""),
      isPrincipal: get(address, "isPrincipal", false) ? "Sí" : "No",
    }));
  }

  export(
    format: ExportFileFormat,
    range: ExportRange,
    totalItems: number,
    pageSize: number,
    pageIndex: number,
    geolocationPoint: string
  ) {
    const mainBatchSize = 500;
    const subBatchSize = 100;
    const totalPages = Math.ceil(totalItems / mainBatchSize);

    this.exportService
      .task({
        id: BUSINESS_PARTNER_ADDRESS_EXPORT_ID,
        title: BUSINESS_PARTNER_ADDRESS_TITLE,
        format: format,
        iterations: range === ExportRange.AllPages ? totalPages : 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(
            BUSINESS_PARTNER_ADDRESS_TITLE,
            LOCALIZED_PROPERTIES_ADDRESS
          );
        },
        next: async (ctx: ExportContext) => {
          const currentPage =
            range === ExportRange.AllPages ? ctx.iteration : pageIndex;

          const response = await this.businessPartnerService
            .findByFilter(
              buildQuery(
                seed(this.businessPartnerService.currentFilter),
                page(currentPage, mainBatchSize)
              ),
              geolocationPoint,
              { contextHeader: "export" }
            )
            .toPromise();

          const items = get(response, "data.0.data", []);

          // Procesar items en sub-batches secuenciales
          for (let i = 0; i < items.length; i += subBatchSize) {
            const subBatch = items.slice(i, i + subBatchSize);
            for (const item of subBatch) {
              ctx.processor.addToSection(
                BUSINESS_PARTNER_ADDRESS_TITLE,
                this.transformItem(item)
              );
            }
            // Evitar bloqueo de UI
            await new Promise((res) => setTimeout(res, 0));
          }

          ctx.next();
        },
      })
      .start();
  }

  canStartTask() {
    if (this.exportService.taskIsActive(BUSINESS_PARTNER_ADDRESS_EXPORT_ID)) {
      this.exportService.setVisible(true);
      return false;
    }
    return true;
  }

  exportAllAddresses(
    format: ExportFileFormat,
    range: ExportRange,
    totalItems: number,
    pageSize: number,
    pageIndex: number
  ): void {
    const totalPages = ceil(totalItems / pageSize) + 1;
    this.exportService
      .task({
        id: BUSINESS_PARTNER_ADDRESS_EXPORT_ID,
        title: BUSINESS_PARTNER_ADDRESS_TITLE,
        format: format,
        iterations: range === ExportRange.AllPages ? totalPages : 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(
            BUSINESS_PARTNER_ADDRESS_TITLE,
            LOCALIZED_PROPERTIES_ALL_ADDRESS
          );
        },
        next: (ctx: ExportContext) => {
          const currentPage =
            range === ExportRange.AllPages ? ctx.iteration : pageIndex;

          this.businessPartnerService
            .findByAllAddresses(
              buildQuery(
                seed(this.businessPartnerService.currentFilterAllAddresses),
                page(currentPage, pageSize)
              ),
              { contextHeader: "export" }
            )
            .pipe(takeUntil(ctx.stopped))
            .subscribe((response) => {
              get(response, "data.0.data", []).forEach((item) => {
                ctx.processor.addToSection(
                  BUSINESS_PARTNER_ADDRESS_TITLE,
                  this.transformItemAllAddress(item)
                );
              });
              ctx.next();
            });
        },
      })
      .start();
  }

  private transformItemAllAddress(item: BusinessPartnerAttributes) {
    const externalKeys = get(item, "externalKeys.code", "");
    const name = get(item, "name", "");
    const addresses = get(item, "addresses", []);
    const subRouteFrequencies = get(item, "subRouteThereAreFrequencies", []);

    const whereFilters = get(
      this.businessPartnerService.currentFilterAllAddresses,
      "where",
      []
    );
    const frequencyFilter = whereFilters.find(
      (f: any) => f["subRouteThereAreFrequencies.id"]
    );
    const currentFilterId = get(
      frequencyFilter,
      '["subRouteThereAreFrequencies.id"].value[0]',
      null
    );

    if (!Array.isArray(addresses) || addresses.length === 0) {
      return [
        {
          externalKeys,
          name,
          code: "",
          address: "",
          longitude: "",
          latitude: "",
          addressType: "",
          country: "",
          department: "",
          city: "",
          isPrincipal: "",
          routeCode: "",
          routeName: "",
          geolocationPointUpdate: "",
        },
      ];
    }

    return addresses.flatMap((address) => {
      const baseAddress = {
        externalKeys,
        name,
        code: get(address, "externalKeys.code", ""),
        address: get(address, "address", ""),
        latitude: get(address, "geolocationPoint[0]", ""),
        longitude: get(address, "geolocationPoint[1]", ""),
        addressType: get(address, "type.name", ""),
        country: get(address, "locationOne.name", ""),
        department: get(address, "locationTwo.name", ""),
        city: get(address, "locationThree.name", ""),
        isPrincipal: get(address, "isPrincipal", false) ? "Sí" : "No",
        geolocationPointUpdate: get(address, "geolocationPointUpdate", false)
          ? "Sí"
          : "No",
      };

      // Si el filtro incluye un ID de frecuencia → mostrar solo esa
      if (currentFilterId != null) {
        const matchedFreq = subRouteFrequencies.find(
          (freq) => get(freq, "id", null) === currentFilterId
        );
        if (matchedFreq) {
          return [
            {
              ...baseAddress,
              routeCode: get(matchedFreq, "externalKeys.code", ""),
              routeName: get(matchedFreq, "name", ""),
            },
          ];
        }
      }

      // Si no hay filtro de frecuencia pero sí existen frecuencias → mostrar todas
      if (subRouteFrequencies.length > 0) {
        return subRouteFrequencies.map((freq) => ({
          ...baseAddress,
          routeCode: get(freq, "externalKeys.code", ""),
          routeName: get(freq, "name", ""),
        }));
      }

      // Si no hay frecuencias → mostrar vacío
      return [
        {
          ...baseAddress,
          routeCode: "",
          routeName: "",
        },
      ];
    });
  }
}
